import { useEffect, useRef, useState } from 'react'
import { Modal } from 'components'
import ErrorMessage from 'components/forms/error-message'
import { lang } from 'config'
import InputAmount from 'components/forms/input-amount'
import Input from 'components/forms/input'
import { toast } from 'react-hot-toast'
import { executeAPI } from 'repositories/db-repository'
import { useAuth } from "contexts/auth-context"
import { useSWRConfig } from "swr"
import { useLocation, useNavigate } from "react-router-dom"
import { getParameter, getMinimumDiscount } from 'helpers/general-helper'
import { insertPurchaseTransaction, updatePurchaseTransaction } from 'helpers/transaction-helper'
import { set } from 'lodash'

/* ============================
   GLOBAL REQUEST TIMEOUT HELPER
   ============================ */
const DEFAULT_TIMEOUT = 15000; // 15s

/** Fetch with timeout (abortable) */
async function timeoutFetch(input, init, ms = DEFAULT_TIMEOUT) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

/** Wrap callback APIs (executeAPI etc.) with timeout */
function withTimeoutCallback(
  register,
  ms = DEFAULT_TIMEOUT
) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const id = setTimeout(() => {
      settled = true;
      reject(new Error('REQUEST_TIMEOUT'));
    }, ms);

    const _resolve = (v) => {
      if (settled) return;
      settled = true;
      clearTimeout(id);
      resolve(v);
    };
    const _reject = (e) => {
      if (settled) return;
      settled = true;
      clearTimeout(id);
      reject(e);
    };

    register(_resolve, _reject);
  });
}

/** Promisified + timeout executeAPI */
const callAPI = (url, action, payload, timeout = DEFAULT_TIMEOUT) =>
  withTimeoutCallback((resolve, reject) => {
    executeAPI(
      url,
      action,
      payload,
      (result, message) => resolve({ result, message }),
      (message) => reject(new Error(message || 'Request gagal'))
    );
  }, timeout);

/** Promisified + timeout insert/update PT */
const callInsertPT = (data, userName, timeout = DEFAULT_TIMEOUT) =>
  withTimeoutCallback((resolve, reject) => {
    insertPurchaseTransaction(
      data,
      userName,
      (result, message) => resolve({ result, message }),
      (message) => reject(new Error(message || 'Insert PT gagal'))
    );
  }, timeout);

const callUpdatePT = (data, userName, timeout = DEFAULT_TIMEOUT) =>
  withTimeoutCallback((resolve, reject) => {
    updatePurchaseTransaction(
      data,
      userName,
      (result, message) => resolve({ result, message }),
      (message) => reject(new Error(message || 'Update PT gagal'))
    );
  }, timeout);

/** =========================
 *  Async Product Unit Picker
 *  ========================= */
function AsyncProductUnitPicker({
  disabled,
  value,
  onChange,
  outlet_group_id,
  outlet_id,
  initialLabel,
}) {
  const perPage = 30
  const [query, setQuery]     = useState('')
  const [page, setPage]       = useState(1)
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [label, setLabel]     = useState(initialLabel || '')
  const [byId, setById]       = useState(new Map())
  const listRef = useRef(null)
  const debounceRef = useRef(null)

  const baseURL = process.env.REACT_APP_INVENTORY_API_URL

  const buildURL = (params) => {
    const url = new URL(`${baseURL}/ps_list`)
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
    })
    return url.toString()
  }

  const fetchPage = async (p = 1, append = false) => {
    if (!baseURL) {
      toast.error('REACT_APP_INVENTORY_API_URL belum diset')
      return
    }
    setLoading(true)
    try {
      const url = buildURL({
        page: p,
        per_page: perPage,
        search: query,
        outlet_group_id,
        only_in_stock: true,
        with_attributes: false,
      })
      const res = await timeoutFetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const rows = Array.isArray(json?.data) ? json.data : []
      const newMap = new Map(byId)
      rows.forEach((r) => newMap.set(r.product_unit_id, r))
      setById(newMap)
      setItems(prev => append ? [...prev, ...rows] : rows)
      setHasMore(Boolean(json?.meta?.next_page_url))
      setPage(p)
    } catch (e) {
      if (e?.name === 'AbortError') toast.error('Timeout ambil data produk')
      else toast.error('Gagal mengambil daftar produk.')
    } finally {
      setLoading(false)
    }
  }

  const fetchById = async (id) => {
    if (byId.has(id)) return byId.get(id)
    try {
      const url = buildURL({
        page: 1,
        per_page: 1,
        outlet_group_id,
        with_attributes: false,
        only_in_stock: false,
        product_unit_id: id,
      })
      const res = await timeoutFetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const row = Array.isArray(json?.data) ? json.data[0] : null
      if (row) {
        const newMap = new Map(byId)
        newMap.set(row.product_unit_id, row)
        setById(newMap)
        return row
      }
    } catch (e) {
      if (e?.name === 'AbortError') toast.error('Timeout ambil data produk')
      else toast.error('Gagal ambil detail produk')
    }
    return null
  }

  useEffect(() => { fetchPage(1, false) }, []) // eslint-disable-line

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchPage(1, false), 300)
    return () => clearTimeout(debounceRef.current)
  }, [query]) // eslint-disable-line

  useEffect(() => {
    (async () => {
      if (!value) return
      const cached = byId.get(value)
      const row = cached || await fetchById(value)
      if (row) setLabel(row.product_unit_long_name || row.product_unit)
    })()
    // eslint-disable-next-line
  }, [value])

  const onScroll = (e) => {
    const el = e.currentTarget
    if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      fetchPage(page + 1, true)
    }
  }

  if (disabled) {
    return <Input disabled value={label || '-'} id="product_unit_label" />
  }

  return (
    <div className="space-y-2">
      <input
        className="w-full px-3 py-2 text-sm border rounded-xl"
        placeholder="Cari SKU / Nama / Brand / Model…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div
        ref={listRef}
        className="h-72 overflow-auto border rounded-xl"
        onScroll={onScroll}
      >
        {items.map((row) => (
          <button
            type="button"
            key={row.product_unit_id}
            className={`w-full text-left px-3 py-2 text-xs hover:bg-neutral-50 ${value === row.product_unit_id ? 'bg-neutral-100' : ''}`}
            onClick={() => {
              setLabel(row.product_unit_long_name || row.product_unit)
              onChange(row.product_unit_id, row)
            }}
          >
            <div className="font-medium">{row.product_unit}</div>
            <div className="text-neutral-500">
              {row.sku} • {row.product_brand} • {Number(row.price ?? 0).toLocaleString('id-ID')}
            </div>
            {row.pre_order === 1
              ? <div className="text-[10px] text-amber-600">PRE ORDER</div>
              : <div className="text-[10px] text-green-600">Stock: {row.current_stock}</div>}
          </button>
        ))}
        {loading && <div className="p-3 text-xs text-neutral-500">Memuat…</div>}
        {!loading && items.length === 0 && <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>}
      </div>
    </div>
  )
}

/** =========================
 *  Content
 *  (mode RETURN ketika purchase.origin === 'RT')
 *  ========================= */
const Content = ({ productUnitSelection, productItems, add, item, data, onSubmit }) => {
  const { user } = useAuth()
  const { cache } = useSWRConfig()
  const location = useLocation()
  const navigate = useNavigate()

  const isReturn = data?.origin === 'RT'
  let ppn_percentage = parseFloat(getParameter("PPN_PERCENTAGE"))

  const [productUnit, setProductUnit] = useState(item?.product_unit_id)
  const [selectedProductUnit, setSelectedProductUnit] = useState(item || null)

  const [quantity, setQuantity] = useState(
    isReturn ? (item?.rtn_quantity ?? 1)
    : (data?.origin === 'PS' ? item?.po_quantity :
       data?.origin === 'RS' ? item?.rcv_quantity : null)
  )

  const [notes, set_notes] = useState(
    isReturn ? (item?.notes ?? '')
    : (data?.origin === 'PS' ? item?.po_notes :
       data?.origin === 'RS' ? item?.rcv_notes : null)
  )

  const [price, setPrice] = useState(
    isReturn ? (item?.rtn_price ?? selectedProductUnit?.price ?? 0) : undefined
  )

  const [returnedQuantity, setReturnedQuantity] = useState(item?.rtn_quantity ?? 0)
  const [errors, setErrors] = useState({})
  const [discount, set_discount] = useState()
  const [nett_price, set_nett_price] = useState()

  // GA states
  const [ga_product_unit_id, set_ga_product_unit_id] = useState()
  const [ga_product_unit_long_name, set_ga_product_unit_long_name] = useState()
  const [ga_item_count, set_ga_item_count] = useState(0)
  const [ga_current_stock_ready, set_ga_current_stock_ready] = useState(true)
  const [total_ga_item_count, set_total_ga_item_count] = useState(0)
  const [arr_ga_product_item, set_arr_ga_product_item] = useState()

  const [cacheById, setCacheById] = useState(new Map())

  const fetchProductById = async (id) => {
    if (!id) return null
    if (cacheById.has(id)) return cacheById.get(id)
    try {
      const baseURL = process.env.REACT_APP_INVENTORY_API_URL
      const url = new URL(`${baseURL}/ps_list`)
      url.searchParams.set('product_unit_id', String(id))
      url.searchParams.set('per_page', '1')
      url.searchParams.set('page', '1')
      url.searchParams.set('with_attributes', '0')
      url.searchParams.set('only_in_stock', '0')
      url.searchParams.set('outlet_group_id', String(data?.outlet_group_id ?? ''))

      const res = await timeoutFetch(url.toString())
      if (!res.ok) return null
      const json = await res.json()
      const row = Array.isArray(json?.data) ? json.data[0] : null
      if (row) {
        const m = new Map(cacheById)
        m.set(row.product_unit_id, row)
        setCacheById(m)
      }
      return row
    } catch {
      return null
    }
  }

  const setProductUnitSelection = (product_unit_id, rowObj) => {
    setProductUnit(product_unit_id)
    setSelectedProductUnit(rowObj || null)

    if (isReturn && rowObj?.price !== undefined) {
      setPrice(Number(rowObj.price) || 0)
    }

    if (rowObj) {
      const m = new Map(cacheById)
      m.set(rowObj.product_unit_id, rowObj)
      setCacheById(m)
      if (!isReturn) calculateDiscount(rowObj, quantity, m)
    }
  }

  /** ---------------------
   *  RETURN helper
   *  --------------------- */
  const upsertReturnItem = async (headerId) => {
    try {
      // edit → hapus dulu
      if (item?.product_unit_id) {
        await callAPI(
          `${process.env.REACT_APP_REMAX_PS_API_URL}`,
          "delete_return_item",
          {
            return_header_id: headerId,
            arr_return_item: JSON.stringify([{ product_unit_id: item.product_unit_id }]),
          },
          DEFAULT_TIMEOUT
        );
      }

      await callAPI(
        `${process.env.REACT_APP_REMAX_PS_API_URL}`,
        "insert_return_item",
        {
          return_header_id: headerId,
          arr_return_item: JSON.stringify([
            {
              product_unit_id: selectedProductUnit?.product_unit_id ?? productUnit,
              product_unit: selectedProductUnit?.product_unit ?? item?.product_unit,
              quantity: Number(quantity || 0),
              price: Number(isReturn ? price : selectedProductUnit?.price || 0),
              notes: notes || null,
            }
          ]),
          created_by: user?.name
        },
        DEFAULT_TIMEOUT
      );

      cache.clear()
      navigate({ pathname: `/return/${headerId}/edit` })
      toast.success(item ? "Item return berhasil diubah!" : "Item return berhasil ditambahkan!", { id: "submit" })
    } catch (e) {
      if (e?.message === 'REQUEST_TIMEOUT') toast.error("Timeout saat simpan Return Item", { id: "submit" })
      else toast.error("onError Return Item: " + e.message, { id: "submit" })
    }
  }

  const ensureReturnHeaderThenAdd = async () => {
    try {
      if (data?.id == null || data?.id === undefined) {
        const { result } = await callAPI(
          `${process.env.REACT_APP_REMAX_PS_API_URL}`,
          "insert_return_header",
          {
            origin: 'RT',
            outlet_group_id: data?.outlet_id,
            outlet_group_name: data?.outlet_group_name,
            outlet_group_phone_number: data?.outlet_group_phone_number,
            user_id: data?.user_id,
            user_name: data?.user_name,
            outlet_id: data?.outlet_id,
            outlet_name: data?.outlet_name,
            outlet_phone_number: data?.outlet_phone_number,
            outlet_email_address: data?.outlet_email_address,
            entry_date: data?.entry_date,

            purchase_header_id: data?.purchase_header_id ?? null, // BY_PO opsional
            purchase_transaction_id: data?.purchase_transaction_id ?? null, // BY_PO opsional

            warehouse_id: data?.warehouse_id,
            warehouse_name: data?.warehouse_name,
            warehouse_address: data?.warehouse_address,

            supplier_id: data?.supplier_id,
            supplier_name: data?.supplier_name,
            supplier_phone_number: data?.supplier_phone_number,
            supplier_email_address: data?.supplier_email_address,
            supplier_address: data?.supplier_address,

            notes: data?.notes,
            ppn_percentage: data?.ppn_percentage ?? 11,
            return_type: data?.return_type ?? 'BY_PRODUCT',

            latest_flow_detail_id: "RETURN_DRAFT",
            flow_detail_id: "RETURN_DRAFT",
            record_status: "I",
            created_by: user?.name
          },
          DEFAULT_TIMEOUT
        );

        const headerId = result?.return_header_id
        if (!headerId) {
          toast.error("Gagal membuat return header.")
          return
        }
        await upsertReturnItem(headerId)
      } else {
        await upsertReturnItem(data?.id)
      }
    } catch (e) {
      if (e?.message === 'REQUEST_TIMEOUT') toast.error("Timeout saat membuat Return Header", { id: "submit" })
      else toast.error("onError Return Header: " + e.message, { id: "submit" })
    }
  }

  /** ---------------------
   *  PO helper
   *  --------------------- */
  const purchaseItem = async () => {
    let maxQuantity = 0
    let updatedPrice = 0
    let updatedDiscount = 0
    let updatedNettPrice

    if (data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_ON_GOING" || data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_RETURN") {
      maxQuantity = Number(item.dlv_quantity)
      updatedPrice = Number(item.dlv_price)
      updatedDiscount = Number(item.dlv_discount)
      updatedNettPrice = Number(item.dlv_nett_price)
      ppn_percentage = Number(item.ppn_percentage)
    } else {
      maxQuantity = Number(selectedProductUnit?.current_stock)
      if (selectedProductUnit?.pre_order === 1) {
        maxQuantity = 999999999
      }
      updatedPrice = Number(selectedProductUnit?.price)
      updatedDiscount = Number(discount)
      updatedNettPrice = Number(nett_price)
    }

    let tmp_ga_quantity_same_pu = 0
    if (
      ga_product_unit_id === selectedProductUnit?.product_unit_id &&
      !(data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_ON_GOING" || data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_RETURN")
    ) {
      tmp_ga_quantity_same_pu = total_ga_item_count
    }

    if (
      quantity !== undefined && (selectedProductUnit || !isReturn) && ga_current_stock_ready &&
      ((Number(quantity) + tmp_ga_quantity_same_pu + Number(returnedQuantity) <= maxQuantity) || !isReturn)
    ) {
      toast.loading("Loading", { id: "submit" })

      try {
        if (item) {
          const tmp_total_amount   = Number(quantity) * updatedPrice
          const tmp_total_discount = Number(quantity) * updatedDiscount
          const tmp_total_nett     = Number(quantity) * Number(nett_price)
          const tmp_total_dpp      = (100 / (100 + ppn_percentage)) * tmp_total_nett
          const tmp_total_ppn      = (ppn_percentage / 100) * tmp_total_dpp

          const tmp_ret_amt   = Number(returnedQuantity) * updatedPrice
          const tmp_ret_disc  = Number(returnedQuantity) * updatedDiscount
          const tmp_ret_nett  = tmp_ret_amt - tmp_ret_disc
          const tmp_ret_dpp   = (100 / (100 + ppn_percentage)) * tmp_ret_nett
          const tmp_ret_ppn   = (ppn_percentage / 100) * tmp_ret_dpp

          await callAPI(
            `${process.env.REACT_APP_REMAX_PS_API_URL}`,
            "update_purchase_item",
            {
              item_type: item?.item_type,
              origin: data?.purchase_header?.origin ?? data?.origin,
              outlet_group_id: data?.purchase_header?.outlet_group_id ?? data?.outlet_group_id,
              outlet_group_name: data?.purchase_header?.outlet_group_name ?? data?.outlet_group_name,
              purchase_header_id: data?.purchase_header?.id ?? data?.purchase_header_id,
              product_type: selectedProductUnit.product_type,
              product_type_id: selectedProductUnit.product_type_id,
              product_brand: selectedProductUnit.product_brand,
              product_brand_id: selectedProductUnit.product_brand_id,
              brand_id: selectedProductUnit.brand_id,
              product_series: selectedProductUnit.product_series,
              product_series_id: selectedProductUnit.product_series_id,
              series_id: selectedProductUnit.series_id,
              product_model: selectedProductUnit.product_model,
              product_model_long_name: selectedProductUnit.product_model_long_name,
              product_model_id: selectedProductUnit.product_model_id,
              product_unit: selectedProductUnit.product_unit,
              product_unit_long_name: selectedProductUnit.product_unit_long_name,
              sku: selectedProductUnit.sku,
              image: selectedProductUnit.image,
              product_unit_id: selectedProductUnit.product_unit_id,
              attributes: selectedProductUnit.attributes,
              discount_type: selectedProductUnit?.discount_type,
              discount_type_id: selectedProductUnit?.discount_type_id,
              ga_product_unit_id: ga_product_unit_id,
              ga_product_unit_long_name: ga_product_unit_long_name,
              ga_item_count: total_ga_item_count,
              arr_ga_product_item: arr_ga_product_item,
              quantity: quantity,
              currency_id: data?.currency_id,
              rate: data?.rate,
              maximum_retail_price: selectedProductUnit.maximum_retail_price,
              dos_date: selectedProductUnit?.pre_order === 0 ? selectedProductUnit.dos_date : null,
              price: updatedPrice,
              discount: updatedDiscount,
              nett_price: updatedNettPrice,
              total_amount: tmp_total_amount,
              total_discount: tmp_total_discount,
              total_nett: tmp_total_nett,
              total_dpp: tmp_total_dpp,
              total_ppn: tmp_total_ppn,
              returned_quantity: returnedQuantity,
              returned_total_amount: tmp_ret_amt,
              returned_total_discount: tmp_ret_disc,
              returned_total_nett: tmp_ret_nett,
              returned_total_dpp: tmp_ret_dpp,
              returned_total_ppn: tmp_ret_ppn,
              ppn_percentage: ppn_percentage,
              notes: notes,
              record_status: "U",
              updated_by: user?.name
            },
            DEFAULT_TIMEOUT
          );
        } else {
          const tmp_total_amount   = Number(quantity) * Number(selectedProductUnit.price)
          const tmp_total_discount = Number(quantity) * Number(discount)
          const tmp_total_nett     = Number(quantity) * Number(nett_price)
          const tmp_total_dpp      = (100 / (100 + ppn_percentage)) * tmp_total_nett
          const tmp_total_ppn      = (ppn_percentage / 100) * tmp_total_dpp

          await callAPI(
            `${process.env.REACT_APP_REMAX_PS_API_URL}`,
            "insert_purchase_item",
            {
              item_type: 'PO',
              origin: data?.purchase_header?.origin ?? data?.origin,
              outlet_group_id: data?.purchase_header?.outlet_group_id ?? data?.outlet_group_id,
              outlet_group_name: data?.purchase_header?.outlet_group_name ?? data?.outlet_group_name,
              purchase_header_id: data?.purchase_header?.id ?? data?.purchase_header_id,
              user_id: data?.purchase_header?.user_id ?? data?.user_id,
              outlet_id: data?.purchase_header?.outlet_id ?? data?.outlet_id,
              product_type: selectedProductUnit.product_type,
              product_type_id: selectedProductUnit.product_type_id,
              product_brand: selectedProductUnit.product_brand,
              product_brand_id: selectedProductUnit.product_brand_id,
              brand_id: selectedProductUnit.brand_id,
              product_series: selectedProductUnit.product_series,
              product_series_id: selectedProductUnit.product_series_id,
              series_id: selectedProductUnit.series_id,
              product_model: selectedProductUnit.product_model,
              product_model_long_name: selectedProductUnit.product_model_long_name,
              product_model_id: selectedProductUnit.product_model_id,
              product_unit: selectedProductUnit.product_unit,
              product_unit_long_name: selectedProductUnit.product_unit_long_name,
              sku: selectedProductUnit.sku,
              image: selectedProductUnit.image,
              product_unit_id: selectedProductUnit.product_unit_id,
              attributes: selectedProductUnit.attributes,
              discount_type: selectedProductUnit?.discount_type,
              discount_type_id: selectedProductUnit?.discount_type_id,
              ga_product_unit_id: ga_product_unit_id,
              ga_product_unit_long_name: ga_product_unit_long_name,
              ga_item_count: total_ga_item_count,
              arr_ga_product_item: arr_ga_product_item,
              quantity: quantity,
              currency_id: data?.currency_id,
              rate: data?.rate,
              maximum_retail_price: selectedProductUnit.maximum_retail_price,
              dos_date: selectedProductUnit?.pre_order === 0 ? selectedProductUnit.dos_date : null,
              price: selectedProductUnit.price,
              discount: discount,
              nett_price: nett_price,
              total_amount: tmp_total_amount,
              total_discount: tmp_total_discount,
              total_nett: tmp_total_nett,
              total_dpp: tmp_total_dpp,
              total_ppn: tmp_total_ppn,
              ppn_percentage: ppn_percentage,
              notes: notes,
              created_by: user?.name
            },
            DEFAULT_TIMEOUT
          );
        }

        cache.clear()
        navigate({ pathname: `/purchase/${data?.purchase_transaction?.id ?? data?.id}/edit` })
        toast.success(item ? "Product berhasil diubah!" : "Product berhasil ditambahkan!", { id: "submit" })
      } catch (e) {
        if (e?.message === 'REQUEST_TIMEOUT') toast.error("Timeout saat simpan item PO", { id: "submit" })
        else toast.error("onError PI: " + e.message, { id: "submit" })
      }
    } else {
      setErrors({
        product_unit: selectedProductUnit ? undefined : `${lang.product_unit} ${lang.field_required}`,
        quantity: quantity !== undefined ? undefined : `${lang.quantity} ${lang.field_required}`,
      })
    }
  }

  const handleSubmit = async () => {
    try {
      if (quantity == null || Number(quantity) < 1) {
        toast.error("Quantity harus diisi dan lebih dari 0", { id: "submit" })
        return
      }else if(selectedProductUnit == null){
        toast.error("Product Unit harus dipilih", { id: "submit" })
        return
      }

      if (isReturn) {
        if (!selectedProductUnit && !productUnit) {
          setErrors({ product_unit: `${lang.product_unit} ${lang.field_required}` })
          return
        }
        if (!(Number(quantity) > 0)) {
          setErrors({ quantity: `${lang.quantity} ${lang.field_required}` })
          return
        }
        if (data?.return_type == 'BY_PO' && !(Number(quantity) > selectedProductUnit?.rtn_quantity - returnedQuantity)) {
          // setErrors({ quantity: `Quantity return tidak boleh kurang dari yang sudah di-return sebelumnya.` })
          toast.error("Quantity return tidak boleh kurang dari yang sudah di-return sebelumnya.", { id: "submit" })
          return
        }
        if (data?.warehouse_name && data?.supplier_name && data?.warehouse_address) {
          toast.loading("Loading", { id: "submit" })
          await ensureReturnHeaderThenAdd()
        } else {
          toast.error("Data return tidak lengkap (warehouse/supplier/address).", { id: "submit" })
        }
        return
      }

      // === PO MODE
      let maxQuantity = 0
      if (data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_ON_GOING" || data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_RETURN") {
        maxQuantity = Number(item.dlv_quantity)
      } else {
        maxQuantity = Number(selectedProductUnit?.current_stock)
        if (selectedProductUnit?.pre_order === 1) maxQuantity = 999999999
      }

      let tmp_ga_quantity_same_pu = 0
      if (ga_product_unit_id === selectedProductUnit?.product_unit_id) {
        tmp_ga_quantity_same_pu = total_ga_item_count
      }

      if (
        quantity !== undefined && (selectedProductUnit || !isReturn) && ga_current_stock_ready &&
        ((Number(quantity) + tmp_ga_quantity_same_pu + Number(returnedQuantity) <= maxQuantity || !isReturn))
      ) {
        if (data.warehouse_name && data.supplier_name && data.warehouse_address) {
          toast.loading("Loading", { id: "submit" })
          if (data?.id !== null && data?.id !== undefined) {
            try {
              const { result } = await callUpdatePT(data, user?.name, DEFAULT_TIMEOUT)
              toast.success("Purchase berhasil disimpan!", { id: "submit" })
              data.id = result?.purchase_transaction_id
              data.purchase_header_id = result?.purchase_header_id
              await purchaseItem()
            } catch (e) {
              if (e?.message === 'REQUEST_TIMEOUT') toast.error('Timeout saat update Purchase Transaction', { id: 'submit' })
              else toast.error("onError PT: " + e.message, { id: "submit" })
            }
          } else {
            try {
              const { result } = await callInsertPT(data, user?.name, DEFAULT_TIMEOUT)
              toast.success("Purchase berhasil disimpan!", { id: "submit" })
              data.id = result?.purchase_transaction_id
              data.purchase_header_id = result?.purchase_header_id
              await purchaseItem()
            } catch (e) {
              if (e?.message === 'REQUEST_TIMEOUT') toast.error('Timeout saat create Purchase Transaction', { id: 'submit' })
              else toast.error("onError PT: " + e.message, { id: "submit" })
            }
          }
        } else {
          toast.error("Data purchase tidak lengkap!", { id: "submit" })
          onSubmit()
        }
      } else {
        await purchaseItem()
      }
    } catch (e) {
      // Guard terakhir supaya tidak ada Uncaught (in promise)
      if (e?.message === 'REQUEST_TIMEOUT') toast.error('Request timeout. Coba lagi.', { id: 'submit' })
      else toast.error('Terjadi kesalahan: ' + e.message, { id: 'submit' })
    }
  }

  const setQuantityAll = (value) => {
    setQuantity(value)
    if (selectedProductUnit && !isReturn) calculateDiscount(selectedProductUnit, value, cacheById)
  }

  const calculateDiscount = async (tmp_selectedProductUnit, tmp_quantity, mapCache) => {
    // untuk PO saja; Return tidak pakai diskon
    let tmp_discount = 0
    let total_discount = 0
    let tmp_quantity_existing = 0
    let tmp_ga_quantity = 0
    let tmp_ga_quantity_existing = 0
    let tmp_ga_as_po_quantity_existing = 0
    let tmp_quantity_all = 0
    let ga_current_stock = 0
    let tmp_total_ga_item_count = 0

    if (productItems) {
      const exist = productItems?.find(
        (row) => row.product_unit_id === tmp_selectedProductUnit.product_unit_id && row.item_type === "PO"
      )
      if (exist) tmp_quantity_existing = Number(exist.po_quantity)
    }

    tmp_quantity_all = add === true ? Number(tmp_quantity) + Number(tmp_quantity_existing) : Number(tmp_quantity)

    for (let i = 1; i <= tmp_quantity_all; i++) {
      tmp_discount = 0
      if (tmp_quantity_all >= Number(tmp_selectedProductUnit.sales_item_count)) {
        if (i >= Number(tmp_selectedProductUnit.starting_item_number)) {
          tmp_discount = getMinimumDiscount(
            Number(tmp_selectedProductUnit.price) * (Number(tmp_selectedProductUnit.discount_percentage) ?? 0) / 100,
            (Number(tmp_selectedProductUnit.max_discount_amount) ?? 0)
          )
        }
      }
      total_discount += tmp_discount
      if (Number(tmp_selectedProductUnit.multiply) === 0) break
    }

    if (tmp_selectedProductUnit?.ga_product_unit_id) {
      tmp_ga_quantity = Math.floor((tmp_quantity_all) / Number(tmp_selectedProductUnit.sales_item_count))
      tmp_ga_quantity_existing = Math.floor((tmp_quantity_existing) / Number(tmp_selectedProductUnit.sales_item_count))

      let gaRow = mapCache.get(tmp_selectedProductUnit.ga_product_unit_id)
      if (!gaRow) gaRow = await fetchProductById(tmp_selectedProductUnit.ga_product_unit_id)
      if (gaRow) {
        set_arr_ga_product_item(JSON.stringify(gaRow))
        ga_current_stock = (gaRow.pre_order == 1) ? 999999999 : Number(gaRow.current_stock ?? 0)
      }
      set_ga_product_unit_id(tmp_selectedProductUnit?.ga_product_unit_id)
      set_ga_product_unit_long_name(tmp_selectedProductUnit?.ga_product_unit_long_name)

      tmp_total_ga_item_count = Number(tmp_selectedProductUnit.ga_item_count) * tmp_ga_quantity
      if (productItems) {
        const existGA = productItems?.find(
          (row) => row.product_unit_id === tmp_selectedProductUnit.ga_product_unit_id && row.item_type === "GA"
        )
        if (existGA) {
          tmp_total_ga_item_count = Number(existGA.po_quantity) +
            Number(tmp_selectedProductUnit.ga_item_count) * (tmp_ga_quantity - tmp_ga_quantity_existing)
        }
      }
      const existGAasPO = productItems?.find(
        (row) => row.product_unit_id === tmp_selectedProductUnit.ga_product_unit_id && row.item_type === "PO"
      )
      if (existGAasPO) {
        tmp_ga_as_po_quantity_existing = Number(existGAasPO.po_quantity)
      }
    } else {
      set_arr_ga_product_item(undefined)
      set_ga_product_unit_id(undefined)
      set_ga_product_unit_long_name(undefined)
    }

    set_ga_item_count(Number(tmp_selectedProductUnit.ga_item_count) * tmp_ga_quantity)
    set_total_ga_item_count(tmp_total_ga_item_count)
    set_ga_current_stock_ready((tmp_total_ga_item_count + tmp_ga_as_po_quantity_existing) <= ga_current_stock)

    if (data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_ON_GOING" || data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_RETURN") {
      set_ga_item_count(-1)
      set_total_ga_item_count(-1)
    }

    const denom = (tmp_quantity_all || 1)
    set_discount(total_discount / denom)
    set_nett_price((Number(tmp_selectedProductUnit.price) * denom - total_discount) / denom)
  }

  return (
    <div className="mt-8 space-y-8">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="product_unit" className="block text-xs text-neutral-700">
              {lang.product_unit}<sup className="text-xs text-red-700"> *</sup>
            </label>

            <AsyncProductUnitPicker
              disabled={Boolean(item)}
              value={productUnit}
              initialLabel={item?.product_unit_long_name || item?.product_unit}
              outlet_group_id={data?.outlet_group_id}
              outlet_id={data?.outlet_id}
              onChange={(id, row) => setProductUnitSelection(id, row)}
            />
            <ErrorMessage error={errors.product_unit} />
          </div>

          {isReturn ? (
            <>
              <div>
                <label className="block text-xs text-neutral-700">Price<sup className="text-xs text-red-700"> *</sup></label>
                <InputAmount
                  isAllowed={(v) => v.floatValue >= 0 || v.floatValue === undefined}
                  onChange={(v) => setPrice(v)}
                  value={parseFloat(String(price ?? 0))}
                  disabled={true}
                  id="rtn_price"
                />
              </div>

              <div>
                <label className="block text-xs text-neutral-700">Qty<sup className="text-xs text-red-700"> *</sup></label>
                <InputAmount
                  isAllowed={(v) => v.floatValue >= 0 || v.floatValue === undefined}
                  onChange={(v) => setQuantity(v)}
                  value={parseFloat(String(quantity ?? 1))}
                  id="rtn_qty"
                />
                <ErrorMessage error={errors.quantity} />
              </div>

              <div className="col-span-2">
                <label className="block text-xs text-neutral-700">Remark</label>
                <Input
                  value={notes ?? ""}
                  onChange={(e) => set_notes(e.target.value)}
                  id="rtn_notes"
                />
              </div>
            </>
          ) : (
            <>
              {selectedProductUnit?.discount_type && (
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-neutral-700">
                    {selectedProductUnit?.discount_type}
                  </label>
                </div>
              )}
              {selectedProductUnit?.pre_order === 1 && (
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-neutral-700">PRE ORDER</label>
                </div>
              )}

              {!(data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_ON_GOING" || data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_RETURN") && (
                <>
                  <div>
                    <label htmlFor="price" className="block text-xs text-neutral-700">
                      {lang.price}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount disabled={true} value={parseFloat(String(selectedProductUnit?.price ?? selectedProductUnit?.po_price))} id="price" />
                    <ErrorMessage error={errors.price} />
                  </div>

                  <div>
                    <label htmlFor="quantity" className="block text-xs text-neutral-700">
                      {lang.quantity}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount
                      isAllowed={(v) => v.floatValue >= 0 || v.floatValue === undefined}
                      onChange={(v) => setQuantityAll(v)}
                      value={parseFloat(String(quantity ?? 0))}
                      id="quantity"
                    />
                    <ErrorMessage error={errors.quantity} />
                  </div>

                  <div>
                    <label htmlFor="discount" className="block text-xs text-neutral-700">
                      {lang.discount}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount disabled={true} value={parseFloat(String(discount ?? 0))} id="discount" />
                    <ErrorMessage error={errors.discount} />
                  </div>

                  <div>
                    <label htmlFor="nett_price" className="block text-xs text-neutral-700">
                      {lang.nett_price}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount disabled={true} value={parseFloat(String(nett_price ?? selectedProductUnit?.po_total_nett))} id="nett_price" />
                    <ErrorMessage error={errors.nett_price} />
                  </div>

                  {ga_product_unit_long_name && (
                    <>
                      <div className="col-span-2">
                        <label htmlFor="ga_product_unit_long_name" className="block text-xs text-neutral-700">
                          {lang.ga_product_unit_long_name}<sup className="text-xs text-red-700"> *</sup>
                        </label>
                        <Input disabled={true} value={ga_product_unit_long_name} id="ga_product_unit_long_name" />
                      </div>

                      <div>
                        <label htmlFor="ga_item_count" className="block text-xs text-neutral-700">
                          {lang.ga_item_count}<sup className="text-xs text-red-700"> *</sup>
                        </label>
                        <InputAmount disabled={true} value={parseFloat(String(ga_item_count ?? 0))} id="ga_item_count" />
                      </div>
                    </>
                  )}

                  {/* {selectedProductUnit?.pre_order === 0 && (
                    <div>
                      <label htmlFor="current_stock" className="block text-xs text-neutral-700">
                        {lang.supplier_stock}<sup className="text-xs text-red-700"> *</sup>
                      </label>
                      <InputAmount disabled={true} value={parseFloat(String(selectedProductUnit?.current_stock ?? 0))} id="current_stock" />
                      <ErrorMessage error={errors.current_stock} />
                    </div>
                  )} */}
                </>
              )}

              {(data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_ON_GOING" || data?.latest_flow_detail_id === "SALES_ORDER_DELIVERY_RETURN") && (
                <>
                  <div>
                    <label htmlFor="quantity" className="block text-xs text-neutral-700">
                      Delivered {lang.quantity}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount disabled value={parseFloat(String(item.dlv_quantity ?? 0))} id="quantity" />
                  </div>

                  <div />

                  <div>
                    <label htmlFor="rcv_qty" className="block text-xs text-neutral-700">
                      {lang.received_quantity}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount
                      isAllowed={(v) => v.floatValue >= 0 || v.floatValue === undefined}
                      onChange={(v) => setQuantity(v)}
                      value={parseFloat(String(quantity ?? 0))}
                      id="rcv_qty"
                    />
                    <ErrorMessage error={errors.quantity} />
                  </div>

                  <div>
                    <label htmlFor="rtn_qty" className="block text-xs text-neutral-700">
                      {lang.returned_quantity}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <InputAmount
                      isAllowed={(v) => v.floatValue >= 0 || v.floatValue === undefined}
                      onChange={(v) => setReturnedQuantity(v)}
                      value={parseFloat(String(returnedQuantity ?? 0))}
                      id="rtn_qty"
                    />
                    <ErrorMessage error={errors.quantity} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="text-xs">
        <button onClick={handleSubmit} className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
          <span>{lang.save}</span>
        </button>
      </div>
    </div>
  )
}

/** =========================
 *  Wrapper Modal
 *  ========================= */
const Add = ({ productUnitSelection, productItems, item, add, purchase, onSubmit }) => {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)

  const openModal = () => {
    if (!(purchase.warehouse_name && purchase.supplier_name && purchase.warehouse_address)) {
      toast.error(purchase?.origin === 'RT' ? "Data return tidak lengkap!" : "Data purchase tidak lengkap!", { id: "submit" })
    } else {
      setIsOpen(true)
    }
  }

  const closeModal = () => setIsOpen(false)

  const handleSubmit = (data) => {
    onSubmit(data)
    closeModal()
  }

  const isReturn = purchase?.origin === 'RT'

  return (
    <>
      {add === true && (
        <button type="button" onClick={openModal} className="bg-neutral-800 inline-flex items-center p-1 text-white text-xs transition rounded active:hover:scale-90">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M12 5l0 14"></path>
            <path d="M5 12l14 0"></path>
          </svg>
          <span>{`${lang.create} ${isReturn ? 'Return Item' : 'Product'}`}</span>
        </button>
      )}
      {add === false && (
        <button type="button" onClick={openModal} className="bg-neutral-800 inline-flex items-center p-1 text-white text-xs transition rounded-full active:hover:scale-90">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M4 20h4l10.5 -10.5a1.5 1.5 0 0 0 -4 -4l-10.5 10.5v4"></path>
            <path d="M13.5 6.5l4 4"></path>
          </svg>
        </button>
      )}

      <Modal
        size="xl"
        isOpen={isOpen}
        onClose={closeModal}
        title={
          add === true
            ? (isReturn ? 'Create Return Item' : lang.create + ' ' + lang.data)
            : (purchase?.origin === 'RS' ? 'Ubah Received Quantity' : (isReturn ? 'Ubah Return Item' : 'Ubah PO Quantity'))
        }
        content={
          <Content
            productUnitSelection={productUnitSelection}
            productItems={productItems}
            add={add}
            item={item}
            data={purchase}
            onSubmit={handleSubmit}
          />
        }
      />
    </>
  )
}

export default Add
